import { FilterKey } from "hooks/useOmnisearchDatagrid";
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { bolt } from "utils/metricsLogger";
import {
  HiddenDisplayToken,
  mergeHiddenDisplayTokens,
  stripHiddenDisplayTokens,
} from "utils/omnisearchUtils";

import {
  commitTypedText,
  hasIncompleteTerm,
  serializeOmnisearch,
  splitOmnisearch,
} from "./omnisearchPills";
import { EMPTY_KEY_ALIASES, isMultiValueKey } from "./pillFilterKeys";

type UseOmnisearchTextInputParams = {
  omnisearch: string;
  /** When true the input holds only free text and filters live in pills. */
  showPills: boolean;
  /** Used to decide whether a typed term joins or replaces an existing filter. */
  columnFilterKeys?: FilterKey[];
  /** Maps a filter key to the key it serializes as, for grids that rename one. */
  keyAliases?: Readonly<Record<string, string>>;
  setOmnisearch: (newVal: string) => void;
  setOmnisearchImmediate?: (newVal: string) => void;
  /**
   * Tokens owned by dedicated toolbar controls, hidden from the input's display
   * but kept in the real omnisearch string (merged back in when the user edits
   * the visible text). See {@link HiddenDisplayToken}. With pills on the input
   * holds only free text, and the pill row applies the same hiding itself.
   */
  hiddenDisplayTokens?: HiddenDisplayToken[];
};

const EMPTY_HIDDEN_DISPLAY_TOKENS: HiddenDisplayToken[] = [];

// Matches the URL store's typing debounce (omnisearchStore DEBOUNCE_MS). The
// hidden-token path debounces here and then writes via setOmnisearchImmediate,
// so the delays replace each other rather than stack.
const HIDDEN_MERGE_DEBOUNCE_MS = 500;

/** What the box shows: only the free text when filters live in pills. */
function inputValueFor(
  omnisearch: string,
  showPills: boolean,
  hiddenDisplayTokens: HiddenDisplayToken[]
): string {
  if (showPills) return splitOmnisearch(omnisearch).freeText;
  return stripHiddenDisplayTokens(omnisearch, hiddenDisplayTokens);
}

/**
 * The debounced write for the hidden-token (plain text, no pills) path.
 *
 * The merge is computed when the debounce FIRES, against the omnisearch as it
 * is at that moment — a toolbar control clicked during the window writes the
 * URL immediately, and a stale merge would resurrect the filter it removed.
 * Tokens that arrived from elsewhere during the window (relative to the
 * omnisearch snapshotted when the edit burst began) are carried forward, not
 * dropped. `hasPending` guards the typed text across ANY number of external
 * omnisearch changes until the flush lands, then clears deterministically.
 */
function useHiddenTokenFlush(
  omnisearch: string,
  hiddenDisplayTokens: HiddenDisplayToken[],
  pushQuery: (next: string, immediate: boolean) => void,
  setValue: (value: string) => void
) {
  // Always-fresh values for the flush-time merge, written post-commit so a
  // discarded concurrent render can never leave them holding uncommitted state.
  const latestOmnisearchRef = useRef(omnisearch);
  const latestTokensRef = useRef(hiddenDisplayTokens);
  useEffect(() => {
    latestOmnisearchRef.current = omnisearch;
    latestTokensRef.current = hiddenDisplayTokens;
  });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef(false);
  // The omnisearch as of the edit burst's first keystroke — what the user's
  // typed text was derived from. Tokens in the live omnisearch that were not
  // visible here are preserved by the merge.
  const baseOmnisearchRef = useRef(omnisearch);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = false;
  }, []);

  useEffect(() => cancel, [cancel]);

  const flush = useCallback(
    (typed: string) => {
      timerRef.current = null;
      pendingRef.current = false;
      const current = latestOmnisearchRef.current;
      const tokens = latestTokensRef.current;
      const merged = mergeHiddenDisplayTokens(typed, current, tokens, baseOmnisearchRef.current);
      if (merged == null) {
        // No safe token placement (mid-edit text hostile at both ends): keep
        // the typed text local and the query unchanged rather than corrupt it.
        return;
      }
      // Snap the box to the canonical display: a hand-typed control-owned
      // token leaves the input and lights up its control instead of being
      // indicated twice; a preserved mid-window filter surfaces as text.
      setValue(stripHiddenDisplayTokens(merged, tokens));
      if (merged === current) return;
      pushQuery(merged, true);
    },
    [pushQuery, setValue]
  );

  const schedule = useCallback(
    (typed: string) => {
      if (!pendingRef.current) {
        baseOmnisearchRef.current = latestOmnisearchRef.current;
      }
      pendingRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => flush(typed), HIDDEN_MERGE_DEBOUNCE_MS);
    },
    [flush]
  );

  /** Consume the pending debounce and write now (Enter). */
  const flushNow = useCallback(
    (typed: string) => {
      if (!pendingRef.current) return;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      flush(typed);
    },
    [flush]
  );

  const enabled = useCallback(() => latestTokensRef.current.length > 0, []);
  const hasPending = useCallback(() => pendingRef.current, []);
  /** The query a clear should write: visible text gone, hidden tokens kept. */
  const clearedQuery = useCallback(
    () =>
      mergeHiddenDisplayTokens("", latestOmnisearchRef.current, latestTokensRef.current) ??
      latestOmnisearchRef.current,
    []
  );

  return useMemo(
    () => ({ schedule, flushNow, cancel, enabled, hasPending, clearedQuery }),
    [schedule, flushNow, cancel, enabled, hasPending, clearedQuery]
  );
}

/**
 * Owns the search input's value and the query updates it drives.
 *
 * The input keeps its own state so a parent re-render can't block typing. With
 * pills on it holds only the free-text part of the query, and a complete
 * `key:value` term the user types is folded into the pills instead. Without
 * pills, `hiddenDisplayTokens` (control-owned filters) are stripped from the
 * display and merged back into the query when the user edits the visible text.
 */
export function useOmnisearchTextInput({
  omnisearch,
  showPills,
  columnFilterKeys,
  keyAliases = EMPTY_KEY_ALIASES,
  setOmnisearch,
  setOmnisearchImmediate,
  hiddenDisplayTokens = EMPTY_HIDDEN_DISPLAY_TOKENS,
}: UseOmnisearchTextInputParams) {
  const isMultiValue = useCallback(
    (key: string) => isMultiValueKey(key, columnFilterKeys ?? [], keyAliases),
    [columnFilterKeys, keyAliases]
  );

  const [value, setValue] = useState(() =>
    inputValueFor(omnisearch, showPills, hiddenDisplayTokens)
  );

  // The query this hook last handed out, alongside the prop it was built from.
  //
  // `query` is what the next edit builds on: typing is debounced, so the prop
  // lags behind by up to the debounce interval, and folding a term from the
  // stale prop would drop whatever hasn't landed yet. `prop` tells this hook's
  // own echo apart from a change made somewhere else (a URL edit, applied
  // filters, a removed pill) — only the latter may overwrite what is typed.
  const [pushed, setPushed] = useState({ prop: omnisearch, query: omnisearch, showPills });

  // pushQuery can fire from a debounced timer whose closure predates the
  // current render; `pushed.prop` must record the prop the write actually
  // supersedes, so read it from a post-commit ref rather than the closure.
  const committedOmnisearchRef = useRef(omnisearch);
  useEffect(() => {
    committedOmnisearchRef.current = omnisearch;
  });

  const pushQuery = useCallback(
    (next: string, immediate: boolean) => {
      setPushed({ prop: committedOmnisearchRef.current, query: next, showPills });
      if (immediate && setOmnisearchImmediate) {
        setOmnisearchImmediate(next);
      } else {
        setOmnisearch(next);
      }
    },
    [setOmnisearch, setOmnisearchImmediate, showPills]
  );

  const hiddenFlush = useHiddenTokenFlush(omnisearch, hiddenDisplayTokens, pushQuery, setValue);

  if (pushed.prop !== omnisearch || pushed.showPills !== showPills) {
    const isOwnEcho = pushed.showPills === showPills && pushed.query === omnisearch;
    setPushed({ prop: omnisearch, query: omnisearch, showPills });
    // A pending hidden-token flush also holds the box: the external change is
    // already visible to the flush, which merges against it without clobbering
    // what is being typed.
    if (!isOwnEcho && !hiddenFlush.hasPending()) {
      setValue(inputValueFor(omnisearch, showPills, hiddenDisplayTokens));
    }
  }

  const currentQuery = pushed.prop === omnisearch ? pushed.query : omnisearch;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const typedText = event.target.value;
      setValue(typedText);

      if (!showPills) {
        if (hiddenFlush.enabled()) {
          // Debounce here and merge at flush time against the live omnisearch;
          // the flush writes immediately, replacing the store's own debounce.
          hiddenFlush.schedule(typedText);
          return;
        }
        pushQuery(typedText, false);
        return;
      }

      // Wait for a half-typed term to finish, so the query isn't rewritten and
      // a pill doesn't flash for `state:C` on the way to `state:CA`.
      if (hasIncompleteTerm(typedText)) {
        return;
      }

      const committed = commitTypedText(currentQuery, typedText, isMultiValue);
      // Only take the box back when a term became a pill. Otherwise leave what
      // was typed exactly as typed, spaces and all.
      if (committed.folded) {
        setValue(committed.freeText);
      }
      // A folded term is a filter change, not a search-as-you-type keystroke, so
      // it lands right away — the pill appears with the space that made it.
      pushQuery(committed.omnisearch, committed.folded);
    },
    [currentQuery, isMultiValue, pushQuery, showPills, hiddenFlush]
  );

  // Enter commits a term typed without a trailing space (pills), or consumes a
  // pending hidden-token debounce so the search applies immediately.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter") {
        return;
      }
      if (!showPills) {
        hiddenFlush.flushNow(value);
        return;
      }
      const committed = commitTypedText(currentQuery, value, isMultiValue);
      if (committed.folded) {
        setValue(committed.freeText);
      }
      pushQuery(committed.omnisearch, true);
    },
    [currentQuery, isMultiValue, pushQuery, showPills, value, hiddenFlush]
  );

  const handleClear = useCallback(() => {
    bolt.startInteraction("omnisearch.clear");
    hiddenFlush.cancel();
    setValue("");
    if (showPills) {
      // With pills the input holds only the free-text search, so clearing it
      // leaves the applied filters alone — the pill row owns those.
      pushQuery(serializeOmnisearch(splitOmnisearch(currentQuery).pairs, ""), true);
      return;
    }
    // Clears only what the input displays: hidden control-owned tokens survive,
    // exactly as they do when the user backspaces the visible text to empty.
    pushQuery(hiddenFlush.clearedQuery(), true);
  }, [hiddenFlush, currentQuery, pushQuery, showPills]);

  return { value, handleChange, handleKeyDown, handleClear };
}
